import {FileAddOutlined} from '@ant-design/icons';
import {Button, Form, Modal, Select, message} from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React, {useEffect, useMemo} from 'react';

import {RqUserLookupAllRole} from '../../../../../ReactQuery/apis/rqAuthAPI';
import {RQAddPatientHistory} from '../../../../../ReactQuery/apis/rqPatientHistory';
import PatientHistoryFormFields from '../PatientHistoryFormFields';

// Converts a dayjs value (or anything) to 'YYYY-MM-DD', or null if empty.
const toDateString = value => (value ? dayjs(value).format('YYYY-MM-DD') : null);

// Returns undefined if every value in the object is empty/undefined/null/'',
// otherwise returns the object as-is. Sending `undefined` means the key is
// dropped entirely, which keeps male-patient payloads free of
// menstrualHistory/obstetricHistory objects.
const stripIfEmpty = obj => {
  if (!obj) {return undefined;}

  const hasValue = Object.values(obj).some(
    v => v !== undefined && v !== null && v !== '',
  );

  return hasValue ? obj : undefined;
};

const isFemale = gender => String(gender || '').toUpperCase() === 'FEMALE';

// Builds the exact payload shape the backend expects from raw antd Form values.
// `patientGender` gates menstrualHistory/obstetricHistory: for male patients
// (or unknown gender) these keys are never sent, regardless of form state.
const buildPatientHistoryPayload = (values, patientGender) => {
  const includeFemaleFields = isFemale(patientGender);

  const menstrualHistory = includeFemaleFields ? stripIfEmpty({
    ...values.menstrualHistory,
    lmp: toDateString(values.menstrualHistory?.lmp),
  }) : undefined;

  const obstetricHistory = includeFemaleFields ? stripIfEmpty({
    ...values.obstetricHistory,
    marriedSince: toDateString(values.obstetricHistory?.marriedSince),
  }) : undefined;

  const examination = stripIfEmpty(values.examination);
  const lifestyle = stripIfEmpty(values.lifestyle);
  const diagnosis = stripIfEmpty(values.diagnosis);

  return {
    patientId: values.patientId,
    symptoms: values.symptoms || [],
    ...(examination && {examination}),
    ...(menstrualHistory && {menstrualHistory}),
    ...(obstetricHistory && {obstetricHistory}),
    ...(lifestyle && {lifestyle}),
    investigation: {
      investigations: values.investigation?.investigations || [],
    },
    ...(diagnosis && {diagnosis}),
  };
};

const AddPatienthistory = ({open, onClose, token, onCreated, defaultPatientId}) => {
  const [form] = Form.useForm();
  const addPatientHistory = RQAddPatientHistory();

  const {data: patients = [], isFetching: patientsLoading} = RqUserLookupAllRole('PATIENT', token);

  const patientOptions = useMemo(
    () => patients.map(p => ({
      value: p.id,
      label: p.fullName,
    })),
    [patients],
  );

  // Pre-select the patient the modal was opened for, once patients have loaded.
  useEffect(() => {
    if (open && defaultPatientId !== undefined && defaultPatientId !== null && patients.length) {
      form.setFieldValue('patientId', defaultPatientId);
    }
  }, [open, defaultPatientId, patients, form]);

  // Track whichever patient is currently selected in the dropdown (falls back
  // to the default) so we know whether to show/send female-only fields.
  const selectedPatientId = Form.useWatch('patientId', form) ?? defaultPatientId;

  const selectedPatient = useMemo(
    () => patients.find(p => String(p.id) === String(selectedPatientId)),
    [patients, selectedPatientId],
  );

  const handleClose = () => {
    // form.resetFields();
    onClose();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const formData = buildPatientHistoryPayload(values, selectedPatient?.gender);

      await addPatientHistory.mutateAsync({formData, token});
      message.success('Patient history added');
      onCreated?.();
      handleClose();
    } catch (err) {
      if (err?.errorFields) {return;}

      message.error(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Failed to add patient history',
      );
    }
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      destroyOnHidden
      width={760}
      title={(
        <span className={'text-primary-700 font-bold text-base flex items-center gap-2'}>
          <FileAddOutlined />
          Add Patient History
        </span>
      )}
      footer={[
        <Button key={'cancel'} onClick={handleClose}>
          Cancel
        </Button>,
        <Button
          key={'submit'}
          type={'primary'}
          className={'btn-primary'}
          loading={addPatientHistory.isPending}
          onClick={handleSubmit}
        >
          Save History
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout={'vertical'}
        className={'mt-2'}
        initialValues={{patientId: defaultPatientId}}
      >
        <Form.Item
          name={'patientId'}
          label={'Patient Name'}
          rules={[{required: true, message: 'Please select a patient'}]}
        >
          <Select
            showSearch
            placeholder={'Search and select patient…'}
            loading={patientsLoading}
            options={patientOptions}
            optionFilterProp={'label'}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            notFoundContent={patientsLoading ? 'Loading patients…' : 'No patients found'}
          />
        </Form.Item>

        <PatientHistoryFormFields form={form} gender={selectedPatient?.gender} />
      </Form>
    </Modal>
  );
};

AddPatienthistory.propTypes = {
  defaultPatientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  open: PropTypes.bool.isRequired,
  token: PropTypes.string,
};

export default AddPatienthistory;
